/* Panelin giriş noktası: oturum kontrolü, gezinme, ekran değiştirme.

   Yönlendirme `location.hash` üzerinden. Yol tabanlı gezinme (`/albumler`)
   sunucu tarafında her yolu `index.html`e düşürmeyi gerektirir; hash bunu
   hiçbir yapılandırma olmadan verir ve panel için tek maliyeti adres
   çubuğundaki `#`. Pages'e ayrı bir `_redirects` kuralı borçlanmaktansa
   kabul edilir.

   **Oturum durumu tutulmuyor, soruluyor.** Panel açılışta korumalı bir uca
   gidiyor; cevap gelirse oturum var. `localStorage`'a "giriş yapıldı" yazmak
   daha hızlı olurdu ama yalan söyleyebilirdi: çerez sekiz saatte ölüyor
   (ADMIN_SESSION_TTL_SECONDS) ve sunucu tarafında iptal edilebiliyor. Tek
   doğru kaynak sunucunun cevabı. */

import { ApiError, auth } from './api.ts';
import { el, qs, replace } from './dom.ts';
import { renderLogin } from './views/login.ts';
import { renderPhotos } from './views/photos.ts';
import { renderAlbums } from './views/albums.ts';
import { renderPublish } from './views/publish.ts';
import { renderHealth } from './views/health.ts';
import { renderTraffic } from './views/traffic.ts';
import { renderDesign } from './views/design.ts';
import { renderJournal } from './views/journal.ts';

export type View = (host: HTMLElement, ctx: AppContext) => void | Promise<void>;

export type AppContext = {
  email: string;
  /** Oturum düştüğünde giriş ekranına döner. Her görünüm bunu çağırabilir. */
  signOut: (reason?: string) => void;
  toast: (message: string, kind?: 'ok' | 'bad') => void;
  /** Bir API hatasını ekranda gösterir; oturum kaybıysa girişe döner. */
  fail: (err: unknown) => void;
};

/** `wide`: ekran panelin okuma genişliğine sığmıyor (Görünüm editörünün üç sütunu). */
const ROUTES: { path: string; label: string; view: View; wide?: boolean }[] = [
  { path: '#/kareler', label: 'Kareler', view: renderPhotos },
  { path: '#/albumler', label: 'Albümler', view: renderAlbums },
  { path: '#/gorunum', label: 'Görünüm', view: renderDesign, wide: true },
  { path: '#/gunluk', label: 'Günlük', view: renderJournal, wide: true },
  { path: '#/yayin', label: 'Yayın', view: renderPublish },
  { path: '#/trafik', label: 'Trafik', view: renderTraffic },
  { path: '#/saglik', label: 'Sağlık', view: renderHealth }
];

const app = qs('#app');

boot();

async function boot(): Promise<void> {
  try {
    await auth.probe();
    /* E-postayı sunucu ayrıca vermiyor; giriş ekranı onu yazdığı için
       oradan geliyor. Bilinmiyorsa boş kalır — kimlik değil, süs. */
    mountShell(sessionStorage.getItem('fg_admin_email') ?? '');
  } catch (err) {
    if (err instanceof ApiError && err.isAuthLoss) {
      mountLogin();
      return;
    }
    /* `ApiError.message` sebebi zaten taşıyor (ağ hatasında "Sunucuya
       ulaşılamadı: …"). Başına bir kez daha yazmak mesajı ikiye katlıyordu. */
    mountLogin(err instanceof ApiError ? err.message : 'Beklenmeyen bir hata oluştu.');
  }
}

function mountLogin(message?: string): void {
  app.removeAttribute('aria-busy');
  renderLogin(app, {
    message,
    onSuccess: (email) => {
      try {
        sessionStorage.setItem('fg_admin_email', email);
      } catch {
        /* Özel pencerede depolama fırlatabilir; e-posta yalnızca gösterim. */
      }
      mountShell(email);
    }
  });
}

function mountShell(email: string): void {
  app.removeAttribute('aria-busy');

  const nav = el('nav', { class: 'side' }, el('p', { class: 'brand', text: 'Frawgraphy' }));
  for (const route of ROUTES) {
    nav.append(el('a', { href: route.path, text: route.label, 'data-path': route.path }));
  }
  nav.append(
    el(
      'footer',
      {},
      email ? el('p', { text: email }) : null,
      el('button', {
        type: 'button',
        text: 'Çıkış',
        onclick: async () => {
          /* Çıkış düşse bile panel kilitlenmeli: sunucudaki satır kalsa da
             kullanıcı ekranda kalmamalı. Sebep sıradan — kapalı sekme yerine
             "çıktım" diyen bir yönetici, çıktığını sanmalı ve sanması doğru
             olmalı; tersi sessiz bir güvenlik hatası. */
          try {
            await auth.logout();
          } catch {
            /* yutuluyor: aşağıdaki yeniden yükleme her hâlükârda kilitler */
          }
          sessionStorage.removeItem('fg_admin_email');
          location.hash = '';
          location.reload();
        }
      })
    )
  );

  const main = el('main', { class: 'main' });
  replace(app, el('div', { class: 'shell' }, nav, main));

  const ctx: AppContext = {
    email,
    signOut: (reason) => {
      sessionStorage.removeItem('fg_admin_email');
      mountLogin(reason ?? 'Oturum sona erdi. Yeniden giriş yapın.');
    },
    toast,
    fail: (err) => {
      if (err instanceof ApiError && err.isAuthLoss) {
        ctx.signOut('Oturum sona erdi. Yeniden giriş yapın.');
        return;
      }
      toast(err instanceof Error ? err.message : 'Beklenmeyen bir hata oluştu.', 'bad');
    }
  };

  const draw = (): void => {
    const hash = location.hash || ROUTES[0]!.path;
    const route = ROUTES.find((r) => hash.startsWith(r.path)) ?? ROUTES[0]!;
    for (const link of nav.querySelectorAll('a')) {
      if (link.dataset['path'] === route.path) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
    replace(main);
    main.classList.toggle('main--wide', Boolean(route.wide));
    void route.view(main, ctx);
  };

  addEventListener('hashchange', draw);
  draw();
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;

function toast(message: string, kind: 'ok' | 'bad' = 'ok'): void {
  document.querySelector('.toast')?.remove();
  const node = el('output', { class: 'toast', 'data-kind': kind, text: message });
  document.body.append(node);
  clearTimeout(toastTimer);
  /* Hata mesajı daha uzun durur: onu okumak gerekiyor, "kaydedildi"yi değil. */
  toastTimer = setTimeout(() => node.remove(), kind === 'bad' ? 9000 : 3500);
}
